/**
 * Server-authoritative Deal lifecycle actions (A2 typed endpoints).
 * Frontend must not invent actions — consume `available_actions` from the API.
 */

export const DEAL_LIFECYCLE_ACTIONS = {
  accept: {
    id: "accept",
    label: "Прийняти пропозицію",
    endpoint: "accept",
    targetStatus: "in_progress",
    destructive: false,
    reasonRequired: false,
    consequence:
      "Угода перейде в статус «В процесі». Скасування пізніше потребуватиме причини.",
  },
  decline: {
    id: "decline",
    label: "Відхилити пропозицію",
    endpoint: "decline",
    targetStatus: "cancelled",
    destructive: true,
    reasonRequired: false,
    consequence:
      "Пропозицію буде відхилено. Угода стане скасованою без можливості відновлення в цьому процесі.",
  },
  withdraw: {
    id: "withdraw",
    label: "Відкликати пропозицію",
    endpoint: "withdraw",
    targetStatus: "cancelled",
    destructive: true,
    reasonRequired: false,
    consequence:
      "Ви відкличете власну пропозицію. Угода стане скасованою.",
  },
  cancel: {
    id: "cancel",
    label: "Скасувати угоду",
    endpoint: "cancel",
    targetStatus: "cancelled",
    destructive: true,
    reasonRequired: true,
    consequence:
      "Угоду буде скасовано. Потрібна коротка причина. Відновлення в цьому процесі неможливе.",
  },
  close: {
    id: "close",
    label: "Закрити угоду",
    endpoint: "close",
    targetStatus: "closed",
    destructive: false,
    reasonRequired: false,
    consequence:
      "Угоду буде закрито як завершену. Подальші зміни статусу в цьому процесі неможливі.",
  },
};

export const DEAL_REASON_MAX_LENGTH = 500;

export function getDealAvailableActions(deal) {
  const keys = Array.isArray(deal && deal.available_actions)
    ? deal.available_actions
    : [];
  return keys
    .map((key) => DEAL_LIFECYCLE_ACTIONS[key])
    .filter(Boolean);
}

export function getDealLifecycleAction(actionId) {
  return DEAL_LIFECYCLE_ACTIONS[actionId] || null;
}

export function isTerminalDealStatus(status) {
  return status === "closed" || status === "cancelled";
}

export function getTerminalDealStatusMessage(status) {
  if (status === "closed") return "Угоду закрито";
  if (status === "cancelled") return "Угоду скасовано";
  return null;
}

export function validateCancelReason(reason) {
  const text = String(reason || "").trim();
  if (!text) {
    return { ok: false, message: "Вкажіть причину скасування." };
  }
  if (text.length > DEAL_REASON_MAX_LENGTH) {
    return {
      ok: false,
      message: `Причина має містити не більше ${DEAL_REASON_MAX_LENGTH} символів.`,
    };
  }
  return { ok: true, reason: text };
}

export function buildLifecycleRequestPayload(actionId, expectedStatus, reason) {
  const action = getDealLifecycleAction(actionId);
  if (!action) return null;
  const payload = { expected_status: expectedStatus };
  if (action.reasonRequired) {
    const validated = validateCancelReason(reason);
    if (!validated.ok) {
      return { error: validated.message };
    }
    payload.reason = validated.reason;
  } else if (reason != null && String(reason).trim()) {
    const text = String(reason).trim();
    if (text.length > DEAL_REASON_MAX_LENGTH) {
      return {
        error: `Причина має містити не більше ${DEAL_REASON_MAX_LENGTH} символів.`,
      };
    }
    payload.reason = text;
  }
  return { payload };
}

export const VIEWER_ROLE_LABELS = {
  seller: "Продавець",
  buyer: "Покупець",
  both: "Продавець і покупець",
};

export function getViewerRoleLabel(role) {
  return VIEWER_ROLE_LABELS[role] || "—";
}

export function getCounterpartUser(deal) {
  if (!deal) return null;
  const role = deal.viewer_role;
  if (role === "seller") return deal.buyer_realtor || null;
  if (role === "buyer") return deal.seller_realtor || null;
  if (role === "both") return deal.seller_realtor || deal.buyer_realtor || null;
  return null;
}
